import { Component, Input, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { QuoteSessionQuery } from '@app/store/session/session.query';
import { CLICK_TYPES, CLICK_LOCATIONS, EVENTS } from '@constants/analytics';
import { Modality } from '@models/Modality';
import { Pet } from '@models/Pet';
import { Quote } from '@models/Quote';
import { AnalyticsService } from '@services/analytics.service';
import { LoggingService } from '@services/logging.service';
import { getFrequencyViewValue } from '@utils/frequency.util';
import { Observable, Subject, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
	selector: 'qe-policy-breakdown',
	templateUrl: './policy-breakdown.component.html',
	styleUrls: ['./policy-breakdown.component.scss'],
})
export class PolicyBreakdownComponent implements OnDestroy {
	@Input() showItemizedInfo: boolean = true;
	@Input() showAddPetButton: boolean = true;
	@Input() showEditButtons: boolean = true;
	@Input() showFullPetInfo: boolean = true;
	@Input() showPetInfoToggle: boolean = false;
	public readonly getFrequencyViewValue = getFrequencyViewValue;
	public pets$: Observable<Pet[]>;
	public quoteDetails$: Observable<Quote>;
	public displayFeeTooltip$: Observable<boolean>;
	public isTooltipHovered: boolean = false;
	private readonly stateSepecificFees: string[] = ['nh'];
	private readonly ngUnsubscribe$: Subject<boolean> = new Subject();

	constructor(
		private readonly dialog: MatDialog,
		private readonly sessionState: QuoteSessionQuery,
		private readonly loggingService: LoggingService,
		private readonly gtmService: AnalyticsService,
	) {
		this.pets$ = this.sessionState.petsState$();
		this.quoteDetails$ = this.sessionState.quoteDetailsState$();
		this.displayFeeTooltip$ = combineLatest([
			this.sessionState.ratingState$(),
			this.sessionState
				.quoteDetailsState$()
				.pipe(map((d) => d.paymentModality)),
		]).pipe(
			map(
				([ratingState, paymentFrequency]) =>
					this.stateSepecificFees.includes(ratingState?.toLowerCase() ?? '') &&
					paymentFrequency === Modality.Monthly
			)
		);
	}

	public addNewPet(): void {
		this.gtmService.push({
			event: EVENTS.CLICK,
			click_location: CLICK_LOCATIONS.SCROLL_BAR_RIGHT,
			click_text: 'Add A Pet',
			click_type: CLICK_TYPES.BUTTON,
			click_id: `${CLICK_TYPES.BUTTON}-${CLICK_LOCATIONS.SCROLL_BAR_RIGHT}-Add-A-Pet`
		});
		import(`@molecules/add-edit-pet/add-edit-pet.component`)
			.then((c) => {
				this.dialog.open(c.AddEditPetComponent, { data: { mode: 'add' } });
			})
			.catch((err) => this.loggingService.error(err));
	}

	ngOnDestroy(): void {
		this.ngUnsubscribe$.next(true);
		this.ngUnsubscribe$.unsubscribe();
	}

	public onTooltipEnter(): void {
		this.isTooltipHovered = true;
		this.gtmService.push({
			event: EVENTS.TOOLTIP,
			tooltip_id: `discount-tooltip`
		});
	}

	public onTooltipLeave(): void {
		this.isTooltipHovered = false;
	}
}
